import { create } from 'zustand';
import { MainChart, MainChartField, MainChartStore } from './types';
import { getStoredMainChartOrDefault, saveMainChart } from './storage';
import { createDebouncedSave } from '@/lib/utils/debouncedPersist';

const debouncedMainChartSave = createDebouncedSave(saveMainChart);

/** Immediately persist any pending debounced main-chart write. */
export const flushMainChartSave = debouncedMainChartSave.flush;

export const useMainChartStore = create<MainChartStore>((set, get) => ({
  mainChart: getStoredMainChartOrDefault(),

  updateMainChart: <K extends MainChartField>(field: K, value: MainChart[K]) => {
    const currentMainChart = get().mainChart;
    const updatedMainChart = {
      ...currentMainChart,
      [field]: value,
    };

    // Update store
    set({ mainChart: updatedMainChart });

    debouncedMainChartSave.schedule(updatedMainChart);
  },

  getMainChart: <K extends MainChartField>(field: K): MainChart[K] => {
    return get().mainChart[field];
  },

  loadMainChart: () => {
    const mainChart = getStoredMainChartOrDefault();
    set({ mainChart });
  },

  addIndicator: (indicator: any) => {
    const currentMainChart = get().mainChart;
    const currentIndicators = currentMainChart.indicators || [];
    
    // Generate ID if not provided
    const id = indicator.id || `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Add ID to indicator
    const indicatorWithId = {
      ...indicator,
      id: id,
    };
    
    const updatedMainChart = {
      ...currentMainChart,
      indicators: [...currentIndicators, indicatorWithId],
    };

    // Update store
    set({ mainChart: updatedMainChart });

    // Save to localStorage
    debouncedMainChartSave.schedule(updatedMainChart);

    // Return the persisted indicator (id may differ from the input).
    return indicatorWithId;
  },

  deleteIndicator: (id: string) => {
    const currentMainChart = get().mainChart;
    const currentIndicators = currentMainChart.indicators || [];
    
    const updatedMainChart = {
      ...currentMainChart,
      indicators: currentIndicators.filter((indicator: any) => indicator.id !== id),
    };

    // Update store
    set({ mainChart: updatedMainChart });

    // Save to localStorage
    debouncedMainChartSave.schedule(updatedMainChart);
  },

  updateMainChartIndicator: (id: string, settings: any) => {
    const currentMainChart = get().mainChart;
    const currentIndicators = currentMainChart.indicators || [];
    
    const updatedIndicators = currentIndicators.map((indicator: any) => {
      if (indicator.id === id) {
        return {
          ...indicator,
          ...settings,
        };
      }
      return indicator;
    });
    
    const updatedMainChart = {
      ...currentMainChart,
      indicators: updatedIndicators,
    };

    // Update store
    set({ mainChart: updatedMainChart });

    // Save to localStorage
    debouncedMainChartSave.schedule(updatedMainChart);
  },

  updateMainChartIndicatorSettings: (indicatorId: string, settings: any) => {
    const currentMainChart = get().mainChart;
    const currentIndicators = currentMainChart.indicators || [];
    
    const updatedIndicators = currentIndicators.map((indicator: any) => {
      if (indicator.id === indicatorId) {
        return {
          ...indicator,
          settings,
        };
      }
      return indicator;
    });
    
    const updatedMainChart = {
      ...currentMainChart,
      indicators: updatedIndicators,
    };

    // Update store
    set({ mainChart: updatedMainChart });

    // Save to localStorage
    debouncedMainChartSave.schedule(updatedMainChart);
  },
}));